// Description: Itemised list of sales with line detail and CSV export

import React, { useContext, useEffect, useState } from 'react'
import { Container, Table, Row, Col, Button, Form } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Loader from '../components/Loader'
import Message from '../components/Message'
import ReportPager from '../components/ReportPager'
import { getSalesList } from '../actions/reportActions'
import { ThemeContext } from '../components/ThemeContext.js'
import { formatCurrency } from '../utils/cart.js'
import { downloadFile } from '../utils/download.js'
import { API_URL } from '../apiUrl.js'
import {
  EMPLOYEE_PERMISSIONS,
  hasEmployeePermission
} from '../constants/employeePermissions.js'

const SalesTransactions = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme } = useContext(ThemeContext)

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [openSale, setOpenSale] = useState(null)
  const [exportError, setExportError] = useState('')

  const { loading, error, data } = useSelector((state) => state.salesList)
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  const canViewReports = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.REPORTS_VIEW
  )

  useEffect(() => {
    if (!userInfo && !ownerInfo) {
      navigate('/login')
    } else if (!canViewReports) {
      navigate('/pos')
    } else {
      dispatch(getSalesList({ from, to, pageNumber }))
    }
  }, [
    dispatch,
    navigate,
    userInfo,
    ownerInfo,
    canViewReports,
    from,
    to,
    pageNumber
  ])

  const changeDate = (setter) => (e) => {
    setter(e.target.value)
    setPageNumber(1)
    setOpenSale(null)
  }

  const exportCsv = async (type) => {
    setExportError('')
    try {
      await downloadFile(
        `${API_URL}/reports/sales/export`,
        { from, to, type, tz: new Date().getTimezoneOffset() },
        `sales-${type}.csv`
      )
    } catch (err) {
      setExportError(err.message)
    }
  }

  const sales = data?.sales || []

  return (
    <Container className='my-3 report-page' style={{ color: theme.text }}>
      <Row className='align-items-center report-page-heading'>
        <Col xs={12} md>
          <h1>Sales transactions</h1>
          <p>Inspect sales, refunds, and item-level details.</p>
        </Col>
        <Col xs={12} md='auto' className='text-md-end report-page-actions'>
          <Button
            size='sm'
            className='my-3 me-2'
            variant='outline-primary'
            onClick={() => exportCsv('sales')}
          >
            Export Sales CSV
          </Button>
          <Button
            size='sm'
            className='my-3 me-2'
            variant='outline-primary'
            onClick={() => exportCsv('items')}
          >
            Export Items CSV
          </Button>
          <Button
            size='sm'
            className='my-3'
            variant='outline-primary'
            onClick={() => navigate('/reports')}
          >
            Go Back
          </Button>
        </Col>
      </Row>

      <Form className='report-filter-panel d-flex flex-wrap align-items-end'>
        <Form.Group>
          <Form.Label className='mb-1'>From</Form.Label>
          <Form.Control
            type='date'
            value={from}
            onChange={changeDate(setFrom)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className='mb-1'>To</Form.Label>
          <Form.Control type='date' value={to} onChange={changeDate(setTo)} />
        </Form.Group>
        <small className='report-filter-hint'>
          Leave dates empty for the last 30 days. Exports use the same dates.
        </small>
      </Form>

      {exportError && <Message variant='danger'>{exportError}</Message>}
      {loading && <Loader />}
      {error && <Message variant='danger'>{error}</Message>}

      {data && (
        <>
          <Table
            striped
            bordered
            hover
            responsive
            size='sm'
            className='my-3 report-table'
          >
            <thead>
              <tr>
                <th>Date</th>
                <th>Receipt</th>
                <th>Cashier</th>
                <th>Units</th>
                <th>Subtotal</th>
                <th>Tax</th>
                <th>Total</th>
                <th>Refunded</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sales.length === 0 && (
                <tr>
                  <td colSpan={9}>No sales in this range.</td>
                </tr>
              )}
              {sales.map((sale) => (
                <React.Fragment key={sale._id}>
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      setOpenSale(openSale === sale._id ? null : sale._id)
                    }
                  >
                    <td>{new Date(sale.createdAt).toLocaleString()}</td>
                    <td>{sale.barcode}</td>
                    <td>{sale.cashier?.name}</td>
                    <td>
                      {sale.items.reduce((sum, l) => sum + l.quantity, 0)}
                    </td>
                    <td>{formatCurrency(sale.subtotal)}</td>
                    <td>{formatCurrency(sale.taxes)}</td>
                    <td>{formatCurrency(sale.total)}</td>
                    <td>{formatCurrency(sale.refundedTotal)}</td>
                    <td>{sale.status}</td>
                  </tr>
                  {openSale === sale._id && (
                    <tr>
                      <td colSpan={9}>
                        <Table size='sm' className='mb-0'>
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Qty</th>
                              <th>Returned</th>
                              <th>Price</th>
                              <th>Discount %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sale.items.map((line) => (
                              <tr key={line.inventory}>
                                <td>
                                  {line.manufacturer} {line.subname} -{' '}
                                  {line.item}
                                </td>
                                <td>{line.quantity}</td>
                                <td>{line.returnedQty}</td>
                                <td>{formatCurrency(line.price)}</td>
                                <td>{line.itemdiscount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
          <small>{data.count} sales. Click a row for its items.</small>
          <ReportPager
            page={data.page}
            pages={data.pages}
            onChange={(n) => {
              setPageNumber(n)
              setOpenSale(null)
            }}
          />
        </>
      )}
    </Container>
  )
}

export default SalesTransactions
